// ── Welle D3c: „Dein Tag“ — nächste/laufende Stunde mit letzter Stunde und Hausaufgabe ──
// Daten: GET /api/heute/dein-tag (D3a, Zeiten aus dem Raster der Lehrkraft). Minuten rechnet
// der Browser jede Viertelminute neu (Berliner Zeit); beim Zustandswechsel (Stunde beginnt/
// endet), beim Datumswechsel und beim Zurückkehren in den Tab wird neu geladen.
import React, { useEffect, useRef, useState } from 'react';
import { MIcon } from '../../../lib/micon';
import { globalNav, oeffneKlassenbuchNachtragen } from '../../../lib/nav';
import { useKlassenbuchSprung } from './sprung';
import { zeitZuMin, fmtZeit } from '../../../lib/zeitraster';
import { Geladen, LadeFehler, ladeNeu, useLader } from './lader';
import { useWidgetGroesse } from './groesse';
import { DeinTagPc } from './DeinTagPc';

export const DEIN_TAG_PFAD = '/api/heute/dein-tag';

type Verweis = { datum: string; stunde: number | null; eintrag_id: number | null };
export type StundeInfo = {
  stunde_nr: number; beginn: string; ende: string;
  klasse_id: number | null; klasse_name: string | null; fach: string; raum: string | null;
  letzte_stunde: (Verweis & { thema: string | null }) | null;
  hausaufgabe: (Verweis & { text: string; aufgegeben_am: string }) | null;
  klassenbuch_eintrag_id?: number | null;
  minuten_bis_beginn?: number; minuten_bis_ende?: number;
};
type TagStunde = {
  stunde_nr: number; beginn: string; ende: string; klasse_id: number | null; klasse_name: string | null;
  fach: string | null; raum: string | null; frei: boolean; vorbei: boolean; eingetragen: boolean;
};
export type DeinTagDaten = {
  datum: string; vorname: string | null;
  ferien: { name: string; bis: string; erster_schultag: string | null } | null;
  countdown: { ferien_name: string; schultage: number } | null;
  zustand: 'vor_unterricht' | 'in_stunde' | 'pause' | 'freistunde' | 'nach_unterricht' | 'schulfrei' | 'kein_stundenplan';
  grund?: 'wochenende' | 'feiertag' | 'ferien' | 'schulfrei' | 'keine_stunden';
  fokus: StundeInfo | null; heute: TagStunde[]; offen_heute: number;
  naechster_schultag: { datum: string; erste_stunde: StundeInfo } | null;
};

// ── Zeit-Helfer (Berliner Ortszeit im Browser) ──
const berlinFmt = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
export function berlinJetzt() {
  const t = Object.fromEntries(berlinFmt.formatToParts(new Date()).map(p => [p.type, p.value]));
  return { datum: `${t.year}-${t.month}-${t.day}`, minuten: (Number(t.hour) % 24) * 60 + Number(t.minute) };
}
const datumObj = (d: string) => new Date(d + 'T12:00:00');
export const tagLang = (d: string) => datumObj(d).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
const plusTage = (d: string, n: number) => { const x = datumObj(d); x.setDate(x.getDate() + n); return x.toLocaleDateString('sv-SE'); };
function tagLabel(ziel: string, heute: string) {
  if (ziel === plusTage(heute, 1)) return 'Morgen';
  const diff = Math.round((datumObj(ziel).getTime() - datumObj(heute).getTime()) / 86400000);
  return diff <= 6 ? datumObj(ziel).toLocaleDateString('de-DE', { weekday: 'long' }) : tagLang(ziel);
}
const raumText = (r: string | null) => !r ? '' : /^(r\b|raum\b)/i.test(r.trim()) ? r.trim() : `Raum ${r.trim()}`;

/** Ein Lader für Kopfzeile UND Widget; hält sich selbst aktuell */
export function useDeinTag(): Geladen<DeinTagDaten> & { jetztMin: number; jetztDatum: string } {
  const g = useLader<DeinTagDaten>(DEIN_TAG_PFAD);
  const [jetzt, setJetzt] = useState(berlinJetzt);
  useEffect(() => {
    const t = window.setInterval(() => setJetzt(berlinJetzt()), 15_000);
    const sichtbar = () => { if (document.visibilityState === 'visible') { setJetzt(berlinJetzt()); void ladeNeu(DEIN_TAG_PFAD); } };
    document.addEventListener('visibilitychange', sichtbar);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', sichtbar); };
  }, []);
  // Zustandswechsel: Stunde beginnt bzw. endet, oder ein neuer Tag → neu laden (einmal je Grenze)
  const d = g.daten;
  const grenze = d?.fokus ? zeitZuMin(d.zustand === 'in_stunde' ? d.fokus.ende : d.fokus.beginn) : null;
  const geladenFuer = useRef<string>('');
  useEffect(() => {
    if (!d) return;
    const schluessel = `${d.datum}|${grenze}`;
    const ueber = (grenze != null && jetzt.minuten >= grenze && jetzt.datum === d.datum) || jetzt.datum > d.datum;
    if (ueber && geladenFuer.current !== schluessel) { geladenFuer.current = schluessel; void ladeNeu(DEIN_TAG_PFAD); }
  }, [d, grenze, jetzt]);
  return { ...g, jetztMin: jetzt.minuten, jetztDatum: jetzt.datum };
}

/** Kopfzeile der Seite: Datum + Ferien bzw. Countdown */
export function datumsZeile(d: DeinTagDaten | undefined, heute: string) {
  const teile = [tagLang(d?.datum ?? heute)];
  if (d?.ferien) teile.push(`${d.ferien.name} bis ${tagLang(d.ferien.bis)}`);
  else if (d?.countdown && d.countdown.schultage > 0)
    teile.push(`Noch ${d.countdown.schultage} ${d.countdown.schultage === 1 ? 'Schultag' : 'Schultage'} bis zu den ${d.countdown.ferien_name}`);
  return teile.join(' · ');
}

function Chips({ s }: { s: StundeInfo }) {
  const sprung = useKlassenbuchSprung();
  const ls = s.letzte_stunde, ha = s.hausaufgabe;
  if (!ls?.thema && !ha) return null;
  const oeffne = (v: Verweis) => sprung(v.datum, v.stunde, s.klasse_id);
  return (
    <div className="ld-dt__chips">
      {ls?.thema && (
        <button type="button" className="ld-dt__chip" onClick={() => oeffne(ls)} title={`Klassenbuch vom ${ls.datum}`}>
          <MIcon name="history" size={15} /><span>Letzte Stunde: {ls.thema}</span>
        </button>
      )}
      {ha && (
        <button type="button" className="ld-dt__chip" onClick={() => oeffne(ha)} title={`Aufgegeben am ${ha.aufgegeben_am}`}>
          <MIcon name="menu_book" size={15} /><span>Hausaufgabe: {ha.text}</span>
        </button>
      )}
    </div>
  );
}

function Lektion({ s }: { s: StundeInfo }) {
  return (
    <div className="ld-dt__lektion">
      {s.klasse_name ? <>{s.klasse_name} <span>{s.fach}</span></> : s.fach}
    </div>
  );
}

// Welle K6: am PC eigene Ansicht (Zeitleiste + Karte); Handy und „kein Stundenplan“ wie bisher
export function DeinTagWidget({ g }: { g: ReturnType<typeof useDeinTag> }) {
  const gr = useWidgetGroesse();
  if (gr.geraet === 'pc' && g.daten && g.daten.zustand !== 'kein_stundenplan') return <DeinTagPc g={g} />;
  return <DeinTagHandy g={g} />;
}

function DeinTagHandy({ g }: { g: ReturnType<typeof useDeinTag> }) {
  const gr = useWidgetGroesse();
  const sprung = useKlassenbuchSprung();
  const breit = gr.geraet === 'pc' && gr.w >= 3;
  const tlRef = useRef<HTMLDivElement | null>(null);
  const d = g.daten;
  // Zeitleiste auf die laufende/nächste Stunde scrollen
  useEffect(() => {
    const el = tlRef.current?.querySelector<HTMLElement>('.ist-hervor');
    if (el && tlRef.current) tlRef.current.scrollLeft = Math.max(0, el.offsetLeft - 12);
  }, [d]);

  if (!d) return g.fehler ? <div className="ld-dt ld-dt--leer"><LadeFehler neu={g.neu} /></div> : <div className="ld-dt ld-dt--leer"><div className="ld-skeleton ld-dt__skelett" /></div>;

  const m = g.jetztMin;
  const kopfJetzt = <div className="ld-dt__jetzt"><MIcon name="schedule" size={14} />Jetzt {fmtZeit(m)}</div>;

  // ── Zustände ohne laufenden Unterricht ──
  if (d.zustand === 'kein_stundenplan') {
    return (
      <div className="ld-dt ld-dt--hinweis">
        <div className="ld-dt__titel">Dein Tag auf einen Blick</div>
        <p className="ld-dt__text">Trag deinen Stundenplan ein, dann siehst du hier jeden Morgen deine nächste Stunde mit Thema und Hausaufgabe der letzten Stunde.</p>
        <div className="ld-dt__knoepfe"><button type="button" className="ld-dt__knopf" onClick={() => globalNav('stundenplan')}><MIcon name="calendar_view_week" size={17} />Stundenplan eintragen</button></div>
      </div>
    );
  }

  const naechster = d.naechster_schultag;
  const naechsterBlock = naechster ? (
    <div className="ld-dt__naechster">
      <div className="ld-dt__naechster-zeile">
        <b>{tagLabel(naechster.datum, d.datum)}, {naechster.erste_stunde.beginn}:</b>{' '}
        {naechster.erste_stunde.klasse_name ? `${naechster.erste_stunde.klasse_name} ` : ''}{naechster.erste_stunde.fach}
      </div>
      <Chips s={naechster.erste_stunde} />
    </div>
  ) : null;

  if (d.zustand === 'schulfrei') {
    const titel = d.grund === 'wochenende' ? 'Wochenende'
      : d.grund === 'ferien' && d.ferien ? `${d.ferien.name} bis ${tagLang(d.ferien.bis)}`
      : d.grund === 'feiertag' ? 'Feiertag'
      : d.grund === 'schulfrei' ? 'Schulfreier Tag'
      : 'Heute kein Unterricht';
    return (
      <div className={`ld-dt ld-dt--ruhig${breit ? ' ist-breit' : ''}`}>
        <div className="ld-dt__titel">{titel}</div>
        {naechsterBlock
          ? <><p className="ld-dt__text ld-dt__text--klein">Zum Vorbereiten:</p>{naechsterBlock}</>
          : <p className="ld-dt__text">Im Kalender ist gerade kein weiterer Unterricht eingetragen.</p>}
      </div>
    );
  }

  if (d.zustand === 'nach_unterricht') {
    const offen = d.heute.find(h => !h.frei && h.vorbei && !h.eingetragen && h.klasse_id);
    return (
      <div className={`ld-dt ld-dt--ruhig${breit ? ' ist-breit' : ''}`}>
        <div className="ld-dt__titel">Für heute geschafft.</div>
        {d.offen_heute > 0 && (
          <div className="ld-dt__offen">
            <span>{d.offen_heute} {d.offen_heute === 1 ? 'Stunde' : 'Stunden'} von heute ohne Klassenbuch-Eintrag</span>
            {offen && (
              <button type="button" className="ld-dt__knopf" onClick={() => sprung(d.datum, offen.stunde_nr, offen.klasse_id)}>
                <MIcon name="edit_note" size={17} />Jetzt nachtragen
              </button>
            )}
            {/* Welle B3: mehrere Stunden auf einmal erzählen */}
            <button type="button" className="ld-dt__knopf ld-dt__knopf--leise" onClick={() => oeffneKlassenbuchNachtragen()}>
              <MIcon name="mic" size={17} />Per Sprache
            </button>
          </div>
        )}
        {naechsterBlock}
        <Zeitleiste d={d} breit={breit} tlRef={tlRef} />
      </div>
    );
  }

  // ── vor_unterricht / in_stunde / pause / freistunde ──
  const f = d.fokus!;
  const laeuft = d.zustand === 'in_stunde';
  const bis = laeuft ? zeitZuMin(f.ende) - m : zeitZuMin(f.beginn) - m;
  const pill = laeuft
    ? `Läuft · noch ${Math.max(0, bis)} Min.`
    : bis >= 60 ? `Um ${f.beginn}` : bis <= 0 ? 'Beginnt jetzt' : bis === 1 ? 'In 1 Minute' : `In ${bis} Minuten`;
  const wo = [raumText(f.raum), `${f.stunde_nr}. Stunde bis ${f.ende}`].filter(Boolean).join(', ');
  return (
    <div className={`ld-dt${breit ? ' ist-breit' : ''}`}>
      <div className="ld-dt__oben">
        <div className="ld-dt__text-block">
          <span className={`ld-dt__pill${laeuft ? ' ist-laufend' : ''}`}><span className="ld-dt__punkt" />{pill}</span>
          <Lektion s={f} />
          <div className="ld-dt__wo">{wo}</div>
          <Chips s={f} />
        </div>
        <div className="ld-dt__knoepfe">
          <button type="button" className="ld-dt__knopf" onClick={() => sprung(d.datum, f.stunde_nr, f.klasse_id)}>
            <MIcon name="menu_book" size={17} />Klassenbuch öffnen
          </button>
        </div>
      </div>
      {kopfJetzt}
      <Zeitleiste d={d} breit={breit} tlRef={tlRef} />
    </div>
  );
}

function Zeitleiste({ d, breit, tlRef }: { d: DeinTagDaten; breit: boolean; tlRef: React.MutableRefObject<HTMLDivElement | null> }) {
  const gr = useWidgetGroesse();
  const sprung = useKlassenbuchSprung();
  if (!d.heute.length) return null;
  const hervor = d.fokus?.stunde_nr ?? null;
  let segmente = d.heute;
  // 2 Spalten am PC: nur die nächsten 3 (ab der hervorgehobenen bzw. ersten nicht vergangenen)
  if (gr.geraet === 'pc' && !breit) {
    const ab = Math.max(0, d.heute.findIndex(h => h.stunde_nr === hervor || !h.vorbei));
    segmente = d.heute.slice(ab, ab + 3);
  }
  return (
    <div className="ld-dt__tl" ref={tlRef} role="list" aria-label="Stunden heute">
      {segmente.map(h => {
        const istHervor = h.stunde_nr === hervor;
        const kl = ['ld-dt__seg'];
        if (h.frei) kl.push('ist-frei');
        if (h.vorbei && !istHervor) kl.push('ist-vorbei');
        if (istHervor) kl.push('ist-hervor');
        const inhalt = (
          <>
            <small>{h.beginn} · {h.stunde_nr}. Std.{h.eingetragen && <MIcon name="check_circle" size={13} fill className="ld-dt__haken" label="eingetragen" />}</small>
            <b>{h.frei ? 'Freistunde' : `${h.klasse_name ? h.klasse_name + ' ' : ''}${h.fach ?? ''}`}</b>
          </>
        );
        return h.frei
          ? <div key={h.stunde_nr} className={kl.join(' ')} role="listitem">{inhalt}</div>
          : <button key={h.stunde_nr} type="button" role="listitem" className={kl.join(' ')}
              onClick={() => sprung(d.datum, h.stunde_nr, h.klasse_id)}>{inhalt}</button>;
      })}
    </div>
  );
}
